import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader, Section } from "@/components/ui-kit";
import { toast } from "sonner";

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", address: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({ name: user.name || "", phone: "", address: "" });
    if (user.role === "CUSTOMER") {
      api.get("/dashboard/customer").then(({ data }) => {
        if (data?.customer) setForm((f) => ({ ...f, phone: data.customer.phone || "", address: data.customer.address || "" }));
      }).catch(() => {});
    }
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault(); setSavingProfile(true);
    try {
      await api.patch("/auth/me", form);
      await refresh();
      toast.success("Profile updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSavingProfile(false); }
  };

  const savePw = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) return toast.error("Passwords don't match");
    if (pw.new_password.length < 8) return toast.error("Min 8 characters");
    setSavingPw(true);
    try {
      await api.patch("/auth/me", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSavingPw(false); }
  };

  const inputCls = "w-full bg-white border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm";

  return (
    <div data-testid="profile-page">
      <PageHeader title="My Profile" subtitle={user?.email} />

      <Section title="Personal info" testId="profile-info">
        <form onSubmit={saveProfile} className="grid md:grid-cols-2 gap-4">
          <div><label className="label block mb-1">Name</label><input required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" /></div>
          <div><label className="label block mb-1">Email (read-only)</label><input readOnly className={inputCls + " bg-muted"} value={user?.email || ""} /></div>
          {user?.role === "CUSTOMER" && (
            <>
              <div><label className="label block mb-1">Phone</label><input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone" /></div>
              <div className="md:col-span-2"><label className="label block mb-1">Address</label><input className={inputCls} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="profile-address" /></div>
            </>
          )}
          <div className="md:col-span-2 flex justify-end">
            <button disabled={savingProfile} className="btn-primary" data-testid="profile-save-btn">{savingProfile ? "Saving…" : "Save profile"}</button>
          </div>
        </form>
      </Section>

      <Section title="Change password" testId="profile-password">
        <form onSubmit={savePw} className="grid md:grid-cols-3 gap-4">
          <div><label className="label block mb-1">Current password</label><input required type="password" className={inputCls} value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="profile-pw-current" /></div>
          <div><label className="label block mb-1">New password</label><input required minLength={8} type="password" className={inputCls} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="profile-pw-new" /></div>
          <div><label className="label block mb-1">Confirm new password</label><input required minLength={8} type="password" className={inputCls} value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="profile-pw-confirm" /></div>
          <div className="md:col-span-3 flex justify-end">
            <button disabled={savingPw} className="btn-primary" data-testid="profile-pw-save-btn">{savingPw ? "Updating…" : "Update password"}</button>
          </div>
        </form>
      </Section>
    </div>
  );
}
